import classnames from 'classnames';
import type React from 'react';
import { type FunctionComponent } from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { useTrackViewedElement } from '@/domains/core/tracking/hooks/useTrackViewedElement';
import { DynamicMDRankingPlaylist } from '@/domains/productDiscovery/ArticleReviews/modules/mdRankingPlaylist/DynamicMDRankingPlaylist';
import { ReviewsBlock } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/ReviewsBlock';
import {
  Block,
  NotABlock,
} from '@/domains/productDiscovery/ProductPage/b2c/components/Block/Block';
import { DescriptionBlock } from '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/DescriptionBlock';
import { MassLandings } from '@/domains/productDiscovery/ProductPage/b2c/components/MassLandings/MassLandings';
import { MainDetails } from '@/domains/productDiscovery/ProductPage/b2c/modules/MainDetails/MainDetails';
import { AdviceBlock } from '@/domains/productDiscovery/ProductPage/b2c/modules/ProductContainer/components/AdviceBlock/AdviceBlock';
import { BrandBlock } from '@/domains/productDiscovery/ProductPage/common/components/BrandBlock/BrandBlock';
import { Breadcrumbs } from '@/domains/productDiscovery/ProductPage/common/components/Breadcrumbs/Breadcrumbs';
import { DeliveryDisclaimer } from '@/domains/productDiscovery/ProductPage/common/modules/Delivery/Disclaimer/DeliveryDisclaimer';
import { ProductComparatorContainer } from '@/domains/productDiscovery/ProductPage/common/modules/ProductComparator/ProductComparator.container';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import { PlaylistProductHistory } from '@/domains/productDiscovery/Recommendation/components/PlaylistGraphQL/components/PlaylistProductHistory/PlaylistProductHistory';
import { ProductBundleContainer } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/ProductBundle.container';
import { useRecommendationFromConfig } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/useRecommendationFromConfig';
import { CarbonScoreDetailContainer } from '@/domains/sustainability/carbonScore/components/CarbonScoreDetail/CarbonScoreDetailContainer';
import { AddToCartModal } from '@/productDiscovery/ProductPage/common/components/AddToCart/AddToCartModal';
import { useFetchSponsoredData } from '@/productDiscovery/ProductPage/modules/SponsoredData/useFetchSponsoredData';
import { TopBannerProductContainer } from '@/sellerAdsManagement/SponsoredProducts/components/TopBannerProduct/TopBannerProduct.container';

import { TabsContainer } from '../TabsContainer/TabsContainer';
import usePlaylistPDP from './components/Playlists/usePlaylistsPDP';
import { breadcrumbsMobileTitle } from './translations';

import styles from './ProductContainer.module.scss';

export const ProductContainer: FunctionComponent = () => {
  const detail = useDetail<'b2c'>();
  const noRecoBrands = [1145];
  const { id } = useBrand();
  const shouldShowExtraPlaylists = !noRecoBrands.includes(id ?? 0);

  /**
   * Used to start recommendation and sponsored recommendation fetching. Used
   by multiple recommendation components in this body ("body" class name).
   */
  const [bodyRef, isInView] = useTrackViewedElement({
    delay: 100,
    threshold: 0,
    rootMargin: '250px', // 250px before the element is in view
    resetViewedWithSearchParams: true,
    triggerOnce: true,
  });

  const [Playlist1, Playlist2, Playlist3] = usePlaylistPDP();

  return (
    <>
      <IsolatedRecommendationInit
        startFetching={isInView}
        key={`sponsored-data-${detail.productId}`}
      />
      <AddToCartModal />
      <TabsContainer />
      <div className={styles.container}>
        <TopBannerProductContainer />
        <Breadcrumbs
          className={classnames(styles.onlyDesktop, styles.topBreadCrumbs)}
        />
        <MainDetails />
        <div ref={bodyRef} className={styles.body}>
          {detail.isSellable && (
            <Block noPadding>
              <ProductBundleContainer />
            </Block>
          )}

          <NotABlock wide>
            <Playlist1 />
          </NotABlock>

          <NotABlock wide>
            <Playlist2 />
          </NotABlock>

          <div
            id="description"
            className={styles.anchor}
            data-testid="description-anchor"
          />

          <DescriptionBlock />

          <Block noPadding>
            <ProductComparatorContainer key={detail.articleId} />
          </Block>

          <NotABlock wide>
            <Playlist3 />
          </NotABlock>

          <div id="reviews-container" className={styles.anchor} />
          <Block>
            <ReviewsBlock />
          </Block>

          <CarbonScoreDetailContainer
            anchorId="carbon-score-detail"
            rootContainer={Block}
          />
          {shouldShowExtraPlaylists && (
            <NotABlock>
              <DynamicMDRankingPlaylist />
            </NotABlock>
          )}

          <NotABlock>
            <AdviceBlock />
          </NotABlock>

          <Block>
            <BrandBlock />
          </Block>

          {shouldShowExtraPlaylists && (
            <NotABlock wide>
              <PlaylistProductHistory productId={detail.productId} />
            </NotABlock>
          )}

          <NotABlock className={styles.onlyMobile}>
            <Text>{breadcrumbsMobileTitle}</Text>
            <Breadcrumbs className={styles.bottomBreadcrumbs} />
          </NotABlock>

          <NotABlock>
            <MassLandings />
          </NotABlock>

          <NotABlock>
            <DeliveryDisclaimer />
          </NotABlock>
        </div>
      </div>
    </>
  );
};

ProductContainer.displayName = 'ProductContainer';

interface IsolatedRecommendationInit {
  startFetching: boolean;
}

/**
 * Used to start recommendation fetching. Isolated
 * from the rest of the component to avoid unnecessary re-renders.
 * @param {boolean} startFetching - Whether to start fetching recommendations.
 * @returns {null} - Nothing to render.
 */
const IsolatedRecommendationInit = ({
  startFetching,
}: IsolatedRecommendationInit): null => {
  useFetchSponsoredData();
  useRecommendationFromConfig(startFetching);
  return null;
};

IsolatedRecommendationInit.displayName = 'IsolatedRecommendationInit';
